import { AsyncSelect, type IAsyncSelectProps } from '@procurify/ui'

import { useFormatPrice, useProcurifyIntl } from '@webapp/hooks'
import { mileageRatePresetRepository } from '@webapp/modules/expense/data-access/repositories/MileageRatePresetRepository'
import { MileageRatePresetUnits } from '@webapp/modules/expense/entities/enums'
import messages from './messages'
import { MileageRatePreset } from '../../entities'

export interface IMileageRatePresetSelectProps<
  IsMulti extends boolean = false,
> extends IAsyncSelectProps<MileageRatePreset, IsMulti> {}

export const MileageRatePresetSelect = ({
  cacheOptions = true,
  clearCacheOnMenuClose = false,
  ...props
}: IMileageRatePresetSelectProps) => {
  const intl = useProcurifyIntl()
  const formatPrice = useFormatPrice()

  const getMileageRatePresetLabel = (value: MileageRatePreset): string => {
    const { unit, rate, currency, name } = value
    const unitLabel = intl.formatMessage(
      unit === MileageRatePresetUnits.MILES
        ? messages.perMile
        : messages.perKilometer
    )

    return `${formatPrice(rate)} ${currency.name} ${unitLabel} - ${name}`
  }

  const loadOptions: IAsyncSelectProps<MileageRatePreset>['loadOptions'] =
    async (inputValue, pagination, callback) => {
      try {
        const res = await mileageRatePresetRepository.list({
          search: inputValue || undefined,
          page: pagination.page,
        })
        const options = res.data.map((preset) => new MileageRatePreset(preset))

        callback({
          options,
          pagination: {
            hasMore: res.pagination
              ? res.pagination.page < res.pagination.numPages
              : false,
            page: pagination.page + 1,
          },
        })
      } catch {
        callback({
          options: [],
          pagination: { hasMore: false, page: pagination.page },
        })
      }
    }

  return (
    <AsyncSelect<MileageRatePreset>
      cacheOptions={cacheOptions}
      clearCacheOnMenuClose={clearCacheOnMenuClose}
      loadOptions={loadOptions}
      placeholder={intl.formatMessage(messages.placeholderText)}
      getOptionLabel={(option) => getMileageRatePresetLabel(option)}
      getOptionValue={(option) => (option ? option.id.toString() : '')}
      {...props}
    />
  )
}
